import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { JiraPeopleOverviewData, JiraPersonDetail } from '@/types/jira'
import { getPeopleOverview, getPersonDetail } from '@/lib/jira'

interface JiraState {
  peopleOverview: JiraPeopleOverviewData | null
  selectedPersonDetail: JiraPersonDetail | null
  isLoadingOverview: boolean
  isLoadingDetail: boolean
  overviewError: string | null
  detailError: string | null
  lastFetchTime: string | null
}

const initialState: JiraState = {
  peopleOverview: null,
  selectedPersonDetail: null,
  isLoadingOverview: false,
  isLoadingDetail: false,
  overviewError: null,
  detailError: null,
  lastFetchTime: null,
}

export const fetchPeopleOverview = createAsyncThunk(
  'jira/fetchPeopleOverview',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getPeopleOverview()
      return data
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch people overview')
    }
  }
)

export const fetchPersonDetail = createAsyncThunk(
  'jira/fetchPersonDetail',
  async (accountId: string, { rejectWithValue }) => {
    try {
      const data = await getPersonDetail(accountId)
      return data
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch person detail')
    }
  }
)

const jiraSlice = createSlice({
  name: 'jira',
  initialState,
  reducers: {
    clearPersonDetail: (state) => {
      state.selectedPersonDetail = null
      state.detailError = null
    },
    clearErrors: (state) => {
      state.overviewError = null
      state.detailError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPeopleOverview.pending, (state) => {
        state.isLoadingOverview = true
        state.overviewError = null
      })
      .addCase(fetchPeopleOverview.fulfilled, (state, action: PayloadAction<JiraPeopleOverviewData>) => {
        state.isLoadingOverview = false
        state.peopleOverview = action.payload
        state.lastFetchTime = new Date().toISOString()
      })
      .addCase(fetchPeopleOverview.rejected, (state, action) => {
        state.isLoadingOverview = false
        state.overviewError = action.payload as string
      })
      .addCase(fetchPersonDetail.pending, (state) => {
        state.isLoadingDetail = true
        state.detailError = null
      })
      .addCase(fetchPersonDetail.fulfilled, (state, action: PayloadAction<JiraPersonDetail>) => {
        state.isLoadingDetail = false
        state.selectedPersonDetail = action.payload
      })
      .addCase(fetchPersonDetail.rejected, (state, action) => {
        state.isLoadingDetail = false
        state.detailError = action.payload as string
      })
  },
})

export const { clearPersonDetail, clearErrors } = jiraSlice.actions
export default jiraSlice.reducer

